'use client';

import type { ComponentProps } from 'react';

import { Popover as PopoverPrimitive } from '@base-ui-components/react/popover';
import { clsxm } from '@vs-shop/utils';
import { createContext, useContext, useState } from 'react';

import { OverlayArrowSvg, overlayArrowSideClasses } from '../overlayArrow';

// Base UI's Popover anchors its Positioner to the Trigger by default and has no
// `Anchor` part. To keep the Radix-style API — an anchored element with the
// popover opened programmatically (no trigger) — `PopoverAnchor` captures its
// wrapper element into this context and `PopoverContent` forwards it to the
// Positioner's `anchor` prop.
const PopoverAnchorContext = createContext<((element: HTMLElement | null) => void) | null>(null);
const PopoverAnchorElementContext = createContext<HTMLElement | null>(null);

export const Popover = (props: ComponentProps<typeof PopoverPrimitive.Root>) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <PopoverAnchorContext.Provider value={setAnchor}>
      <PopoverAnchorElementContext.Provider value={anchor}>
        <PopoverPrimitive.Root {...props} />
      </PopoverAnchorElementContext.Provider>
    </PopoverAnchorContext.Provider>
  );
};

export const PopoverTrigger = (props: ComponentProps<typeof PopoverPrimitive.Trigger>) => (
  <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
);

export const PopoverAnchor = ({ children, ...props }: ComponentProps<'span'>) => {
  const setAnchor = useContext(PopoverAnchorContext);

  return (
    <span data-slot="popover-anchor" ref={setAnchor ?? undefined} {...props}>
      {children}
    </span>
  );
};

// Base UI's Popover.Arrow renders a positioned <div>; the triangle + per-side
// placement are shared with Tooltip via overlayArrow. Consumer-placed (not baked
// into PopoverContent) since some popovers (the comparison ones) show no arrow.
export const PopoverArrow = ({
  className,
  ...props
}: ComponentProps<typeof PopoverPrimitive.Arrow>) => (
  <PopoverPrimitive.Arrow
    data-slot="popover-arrow"
    className={clsxm(overlayArrowSideClasses, className)}
    {...props}
  >
    <OverlayArrowSvg />
  </PopoverPrimitive.Arrow>
);

export type PopoverContentProps = ComponentProps<typeof PopoverPrimitive.Popup> &
  Pick<
    ComponentProps<typeof PopoverPrimitive.Positioner>,
    'side' | 'sideOffset' | 'align' | 'alignOffset'
  >;

export const PopoverContent = ({
  className,
  side,
  sideOffset = 4,
  align = 'center',
  alignOffset,
  children,
  ...props
}: PopoverContentProps) => {
  const anchor = useContext(PopoverAnchorElementContext);

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        data-slot="popover-positioner"
        className="z-50"
        sideOffset={sideOffset}
        align={align}
        {...(side === undefined ? {} : { side })}
        {...(alignOffset === undefined ? {} : { alignOffset })}
        {...(anchor === null ? {} : { anchor })}
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={clsxm(
            'max-w-96 rounded-xs border p-4 text-xs/5 tracking-tight outline-hidden',
            'border-background-intense-more bg-background text-foreground shadow-xl',
            'origin-(--transform-origin) transition-[transform,scale,opacity] duration-150',
            'data-starting-style:scale-95 data-starting-style:opacity-0',
            'data-ending-style:scale-95 data-ending-style:opacity-0',
            className
          )}
          {...props}
        >
          {children}
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
};
